// HEAL-3 / HEAL-69 — юридические документы HealthLab. Единственный источник правды.
//
// Раньше на /legal/* лежали короткие DRAFT-заглушки с плашкой «финал готовит юрист».
// Здесь — финальные тексты (v1 от 04.08.2026, вступают в силу 20.07.2026).
//
// Почему не отдельный .md: копия текста в двух местах неизбежно разъезжается, а это
// документ, по которому у пользователя возникают права. Один файл, версионируется git'ом.
//
// ⚠️ ВЕРСИЯ. LEGAL_VERSION обязана совпадать с `healthlab.current_consent_version` на
// бэкенде: пользователь должен соглашаться ровно с той редакцией, которую видит на экране.
// Расхождение ловит тест legal.version.test.ts.
//
// HEAL-69 (25.09.2026): юрист прислал обновлённую редакцию — сменились реквизиты
// оператора (убран БИН, другой адрес, контакт privacy@ вместо info@) и часть
// формулировок. Версию по решению PM НЕ поднимали: пользователей не уведомляем и
// повторное согласие не собираем. Помните про последствие — поднятие LEGAL_VERSION
// выбрасывает КАЖДОГО действующего пользователя на экран /consent (useOnboardingState
// сравнивает users.consent_version с текущей), поэтому решение осознанное, а не забытый
// bump. Если следующая правка будет существенной — поднимайте обе константы.
//
// Два места документа сознательно расходятся с текстом юриста — он описывал сбор данных,
// которого в приложении нет (решение PM 25.09.2026, повторяет решение от 05.08.2026):
//   §5 Политики / §7.1 Условий — при регистрации собираем email, пароль, имя и фамилию.
//     Телефон не собираем вовсе; пол и дата рождения живут в анкете после регистрации.
//   §17 Политики — кнопка в приложении удаляет аккаунт целиком, а не «профиль»
//     отдельно от аккаунта (см. pages/DeleteAccount.tsx).
// Оба места стережёт legal.version.test.ts — не «чините» их обратно по тексту юриста.

export const LEGAL_VERSION = 'v1-2026-08-04';
export const LEGAL_VERSION_LABEL = 'v1 от 04.08.2026';
export const LEGAL_EFFECTIVE_DATE = '20.07.2026';
export const LEGAL_CONTACT_EMAIL = 'privacy@healthlab.kz';

export type LegalBlock =
  | { kind: 'text'; text: string }
  | { kind: 'list'; items: string[] };

export type LegalSection = {
  title: string;
  blocks: LegalBlock[];
};

export type LegalDoc = {
  title: string;
  lead: string;
  sections: LegalSection[];
};

const t = (text: string): LegalBlock => ({ kind: 'text', text });
const l = (...items: string[]): LegalBlock => ({ kind: 'list', items });

// Без точки на конце: константа встраивается и в середину предложения, и в строку
// реквизитов. Точку ставит то место, где она нужна.
const ADDRESS = 'Республика Казахстан, г. Астана, ул. Сыганак 15';

export const privacyPolicy: LegalDoc = {
  title: 'Политика конфиденциальности',
  lead: 'Определяет, как Health Lab KZ собирает, использует, хранит и защищает персональные данные пользователей приложения и связанных сервисов.',
  sections: [
    {
      title: '1. Название и дата вступления в силу',
      blocks: [
        t('Настоящая Политика конфиденциальности (далее — «Политика») определяет, как Health Lab KZ собирает, использует, хранит и защищает персональные данные пользователей мобильного приложения и связанных сервисов Health Lab KZ. Политика вступает в силу с даты, указанной выше, и действует до её замены новой редакцией.'),
      ],
    },
    {
      title: '2. Сведения об операторе',
      blocks: [
        t('Оператором персональных данных является Индивидуальный предприниматель, осуществляющий деятельность под наименованием (брендом) «Health Lab KZ».'),
        t(`Адрес (юридический и фактический): ${ADDRESS}.`),
        t(`Контакты по вопросам обработки персональных данных: ${LEGAL_CONTACT_EMAIL}.`),
      ],
    },
    {
      title: '3. Область действия',
      blocks: [
        t('Политика распространяется на мобильное приложение Health Lab KZ и на данные, которые пользователь предоставляет при регистрации, использовании функций приложения и обращении в службу поддержки.'),
      ],
    },
    {
      title: '4. Термины',
      blocks: [
        l(
          'Пользователь — физическое лицо, создавшее аккаунт в Health Lab KZ.',
          'Маркер — единица данных, полученная из бланка (например, конкретный показатель крови).',
          'Бланк — лабораторный отчёт или иной документ с результатами анализов, загружаемый пользователем.',
          'Динамика — изменение значений маркеров пользователя во времени.',
          'Дашборд — основной интерфейс приложения, где пользователь видит свои маркеры и динамику.',
          'Персональные данные — любые сведения, относящиеся к определённому или определяемому пользователю, зафиксированные на электронном или ином носителе, включая биометрические данные.',
        ),
      ],
    },
    {
      title: '5. Какие данные собираются',
      blocks: [
        t('При регистрации:'),
        // Расходится с текстом юриста сознательно (решение PM 25.09.2026, повторяет
        // решение 05.08.2026): телефон мы не собираем, email — основной идентификатор,
        // пол и дата рождения относятся к анкете после регистрации.
        l(
          'адрес электронной почты;',
          'пароль (хранится только в зашифрованном виде);',
          'имя и фамилия.',
        ),
        t('Профиль здоровья, который пользователь указывает или загружает после регистрации:'),
        l(
          'пол и дата рождения;',
          'рост и вес;',
          'семейная история здоровья (наличие проблем со здоровьем у близких родственников);',
          'хронические диагнозы;',
          'сведения об операциях и госпитализациях;',
          'загружаемые бланки (фото или файлы результатов анализов, преимущественно связанных с кровью) и распознанные из них маркеры — до 222 биомаркеров, поддерживаемых системой.',
        ),
        t('При использовании приложения:'),
        l(
          'история использования приложения, необходимая для работы дашборда и построения динамики;',
          'обезличенные технические данные, которые собирают аналитические инструменты приложения (см. раздел 11).',
        ),
        t('Health Lab KZ не собирает результаты генетического (ДНК) тестирования, данные документов, удостоверяющих личность, аудио- или видеозаписи пользователя, если это прямо не указано отдельно при запуске соответствующей функции.'),
      ],
    },
    {
      title: '6. Источники данных',
      blocks: [
        t('Health Lab KZ получает персональные данные напрямую от пользователя — при регистрации, заполнении профиля здоровья и загрузке бланков.'),
        t('Health Lab KZ в настоящее время не получает данные пользователя от третьих лиц (лабораторий, работодателей, рекламных партнёров или иных источников).'),
      ],
    },
    {
      title: '7. Цели и основания обработки',
      blocks: [
        t('Health Lab KZ обрабатывает персональные данные для следующих целей:'),
        l(
          'регистрация и идентификация пользователя, обеспечение работы аккаунта;',
          'распознавание маркеров из загруженных бланков и отображение их на дашборде;',
          'построение динамики маркеров и подготовка образовательных AI-комментариев о том, что означает изменение конкретного маркера, с учётом профиля здоровья пользователя (пол, возраст, рост, вес, семейная история, хронические диагнозы);',
          'обеспечение работы и улучшение аналитических инструментов приложения;',
          'развитие и улучшение моделей распознавания и интерпретации маркеров, включая использование загруженных пользователем данных и обезличенной аналитики для исследований и повышения качества сервиса;',
          'ответы на обращения пользователя в поддержку.',
        ),
        t('Основанием обработки является согласие пользователя, полученное при регистрации, заполнении профиля здоровья и/или загрузке бланков.'),
      ],
    },
    {
      title: '8. Согласие пользователя',
      blocks: [
        t('Используя Health Lab KZ, пользователь даёт согласие на обработку своих персональных данных, включая сведения о здоровье, в целях, описанных в разделе 7.'),
        t('Срок действия согласия не превышает срок, необходимый для достижения целей сбора и обработки данных. Если срок не указан отдельно, согласие действует до момента, пока пользователь пользуется приложением, либо до его отзыва.'),
        t('Health Lab KZ вправе запрашивать отдельное согласие для новых целей обработки, не описанных в действующей редакции Политики.'),
      ],
    },
    {
      title: '9. Медицинские документы и сведения о здоровье',
      blocks: [
        t('Пользователь может указывать в профиле сведения о своём здоровье (пол, рост, вес, семейную историю, хронические диагнозы, сведения об операциях и госпитализациях) и загружать бланки с результатами анализов, преимущественно связанных с кровью. Health Lab KZ распознаёт из бланков маркеры и отображает их значения рядом с референсными нормами, а также показывает динамику изменения маркера во времени с учётом профиля здоровья пользователя.'),
        t('Health Lab KZ — не медицинская организация. Приложение не ставит диагнозы, не заменяет консультацию врача и не даёт рекомендаций по лечению или лекарствам. При отклонении маркера от нормы или значимом изменении динамики приложение рекомендует пользователю проконсультироваться с врачом.'),
        t('Сведения о здоровье пользователя (профиль здоровья и загруженные бланки) относятся к особой категории персональных данных и обрабатываются с усиленным контролем доступа. Такие данные не передаются третьим лицам без отдельного согласия пользователя.'),
      ],
    },
    {
      title: '10. Автоматизированная обработка и интерпретации',
      blocks: [
        t('Health Lab KZ использует автоматизированное распознавание бланков и генерацию образовательных AI-комментариев к маркерам. Эти комментарии:'),
        l(
          'объясняют простым языком, что означает изменение маркера (рост, снижение, стабильность) и почему это может быть важно;',
          'не являются медицинским заключением или диагнозом;',
          'не используют тревожные формулировки.',
        ),
        t('Пользователь может обратиться в поддержку, если считает, что распознавание бланка или интерпретация маркера содержит ошибку.'),
      ],
    },
    {
      title: '11. Cookies и аналитика',
      blocks: [
        t('Health Lab KZ использует технические и аналитические инструменты для обеспечения работы приложения и его улучшения. Такие инструменты могут собирать обезличенные данные об использовании приложения (например, какие экраны и функции используются, как часто открывается приложение).'),
      ],
    },
    {
      title: '12. Передача подрядчикам и третьим лицам',
      blocks: [
        t('Health Lab KZ не передаёт персональные данные пользователей врачам, лабораториям или иным медицинским/коммерческим партнёрам. При этом для работы приложения могут привлекаться технические подрядчики (хостинг, аналитика, сервисы распознавания бланков), которые обрабатывают данные от имени и по поручению Health Lab KZ.'),
      ],
    },
    {
      title: '13. Трансграничная передача',
      blocks: [
        t('Health Lab KZ может передавать персональные данные пользователей за пределы Республики Казахстан в связи с использованием облачных сервисов, аналитических инструментов и/или сервисов автоматизированного распознавания бланков. Такая передача осуществляется на основании согласия пользователя и с учётом мер защиты, предусмотренных законодательством Республики Казахстан.'),
      ],
    },
    {
      title: '14. Хранение и сроки',
      blocks: [
        t('Health Lab KZ хранит персональные данные пользователя, включая базу данных, физически размещённую на территории Республики Казахстан, в течение времени, пока действует его аккаунт, а также в течение периода, необходимого для выполнения целей обработки, указанных в разделе 7, и требований законодательства Республики Казахстан.'),
        t('После удаления аккаунта или отзыва согласия персональные данные удаляются или обезличиваются, за исключением случаев, когда более длительное хранение прямо требуется законодательством.'),
      ],
    },
    {
      title: '15. Защита данных',
      blocks: [
        t('Health Lab KZ применяет организационные и технические меры защиты персональных данных, включая шифрование данных при передаче и хранении, а также иные меры, соответствующие применимым стандартам и сертификациям безопасности. Несмотря на принимаемые меры, Health Lab KZ не может гарантировать абсолютную защиту данных при передаче через интернет.'),
      ],
    },
    {
      title: '16. Права пользователя и порядок обращения',
      blocks: [
        t('Пользователь Health Lab KZ вправе:'),
        l(
          'получить информацию о том, какие его персональные данные обрабатываются;',
          'запросить копию своих персональных данных;',
          'потребовать исправления неточных данных;',
          'отозвать согласие на обработку персональных данных;',
          'потребовать удаления своего профиля, аккаунта и связанных данных.',
        ),
        t('Для реализации этих прав пользователь может обратиться по контактам, указанным в разделе 20.'),
      ],
    },
    {
      title: '17. Отзыв согласия и удаление данных',
      blocks: [
        // Юрист написал «удалить свой профиль в настройках», разведя профиль и аккаунт.
        // В приложении кнопка одна и удаляет аккаунт целиком (pages/DeleteAccount.tsx) —
        // документ описывает то, что есть, иначе человек пойдёт писать письмо за тем,
        // что делается кнопкой.
        t('Пользователь может самостоятельно удалить свой аккаунт в настройках приложения Health Lab KZ. Вместе с аккаунтом удаляются профиль здоровья, загруженные бланки и распознанные маркеры.'),
        t('Для отзыва согласия на обработку персональных данных пользователь также может обратиться по контактам, указанным в разделе 20. Обращение можно направить с адреса электронной почты, указанного при регистрации.'),
        t('В соответствии с законодательством Республики Казахстан, Health Lab KZ обязан прекратить обработку персональных данных в течение 15 (пятнадцати) рабочих дней с момента получения отзыва согласия, если хранение или обработка данных не требуется в силу закона, либо предоставить пользователю мотивированный отказ.'),
        t('После удаления профиля или аккаунта персональные данные пользователя удаляются или обезличиваются, за исключением случаев, когда более длительное хранение прямо требуется законодательством.'),
      ],
    },
    {
      title: '18. Несовершеннолетние',
      blocks: [
        t('Health Lab KZ может использоваться несовершеннолетними пользователями. Использование сервиса лицом младше 18 лет допускается с согласия законного представителя (родителя или опекуна). Учитывая, что сервис обрабатывает данные о здоровье, Health Lab KZ рекомендует законным представителям контролировать использование сервиса несовершеннолетними.'),
      ],
    },
    {
      title: '19. Изменения политики',
      blocks: [
        t('Health Lab KZ может время от времени обновлять настоящую Политику. Дата последнего обновления указывается в начале документа. Существенные изменения будут доведены до пользователей через приложение или иным доступным способом. Продолжение использования Health Lab KZ после вступления изменений в силу означает согласие пользователя с обновлённой редакцией.'),
      ],
    },
    {
      title: '20. Контакты',
      blocks: [
        t('По вопросам обработки персональных данных и реализации прав, описанных в разделах 16–17, пользователь может обратиться:'),
        t(`Email: ${LEGAL_CONTACT_EMAIL}`),
        t(`Адрес: ${ADDRESS}.`),
      ],
    },
  ],
};

export const termsOfUse: LegalDoc = {
  title: 'Условия использования',
  lead: 'Регулируют отношения между Health Lab KZ и пользователем приложения. Регистрация означает принятие настоящих Условий.',
  sections: [
    {
      title: 'Общее',
      blocks: [
        t('Добро пожаловать в Health Lab KZ.'),
        t(`Настоящие Условия использования (далее — «Условия») содержат основные положения, касающиеся использования вами нашего Сервиса, включая любые материалы, данные, информацию и контент, доступные через него. ПОЖАЛУЙСТА, ВНИМАТЕЛЬНО ОЗНАКОМЬТЕСЬ С УСЛОВИЯМИ ПЕРЕД ИСПОЛЬЗОВАНИЕМ СЕРВИСА. Мы рекомендуем связаться с нами по адресу ${LEGAL_CONTACT_EMAIL} с любыми вопросами или сомнениями, прежде чем продолжать использование Сервиса.`),
        t('Мы оставляем за собой право изменять или дополнять настоящие Условия в любое время. В случае внесения изменений мы опубликуем обновлённую редакцию с указанием даты последнего обновления в начале документа и уведомим вас о существенных изменениях через интерфейс Сервиса, по электронной почте или иным разумным способом. Если иное не указано в самом обновлении, изменения вступают в силу немедленно. Вам следует периодически знакомиться с действующей редакцией Условий. Если вы не согласны с настоящими или любыми будущими Условиями, вашим единственным средством защиты является отказ от доступа к Сервису и прекращение его использования. Дальнейшее использование Сервиса после даты вступления изменений в силу означает ваше согласие с обновлённой редакцией.'),
        t('Настоящие Условия включают в себя Политику конфиденциальности Health Lab KZ в полном объёме посредством ссылки — как если бы она была полностью изложена здесь, и все ссылки на настоящие Условия относятся, в числе прочего, к Политике конфиденциальности. Пожалуйста, внимательно ознакомьтесь с Политикой конфиденциальности.'),
        t('При использовании отдельных функций Сервиса на вас могут распространяться дополнительные условия, применимые именно к таким функциям («Дополнительные условия»), которые могут время от времени публиковаться в Сервисе или предоставляться вам для принятия при доступе к соответствующей функции. Такие условия считаются включёнными в настоящие Условия посредством ссылки. В случае противоречия между настоящими Условиями и Дополнительными условиями, Дополнительные условия имеют преимущественную силу в отношении соответствующей функции — но только в той мере, в какой это противоречие возникает.'),
      ],
    },
    {
      title: '1. Общие положения',
      blocks: [
        t('Настоящие Условия использования (далее — «Условия») регулируют отношения между Индивидуальным предпринимателем, осуществляющим деятельность под наименованием (брендом) «Health Lab KZ» (далее — «Health Lab KZ»), и физическим лицом, использующим мобильное приложение Health Lab KZ (далее — «Пользователь»).'),
        t('Условия являются публичной офертой в соответствии со статьёй 395 Гражданского кодекса Республики Казахстан. Регистрация в приложении Health Lab KZ и/или начало использования его функций означает полное и безоговорочное принятие (акцепт) Пользователем настоящих Условий.'),
        t('Условия действуют совместно с Политикой конфиденциальности Health Lab KZ, которая регулирует обработку персональных данных Пользователя и является неотъемлемой частью настоящих Условий.'),
        t('Если Пользователь не согласен с Условиями, он должен прекратить использование Health Lab KZ.'),
      ],
    },
    {
      title: '2. Термины и определения',
      blocks: [
        l(
          'Сервис — мобильное приложение Health Lab KZ, включая все его функции и материалы.',
          'Пользователь — физическое лицо, создавшее аккаунт в Health Lab KZ.',
          'Аккаунт — учётная запись Пользователя в Сервисе.',
          'Маркер — единица данных, полученная из бланка (например, конкретный показатель крови).',
          'Бланк — лабораторный отчёт или иной документ с результатами анализов, загружаемый Пользователем.',
          'Динамика — изменение значений маркеров Пользователя во времени.',
          'Дашборд — основной интерфейс Сервиса, где Пользователь видит свои маркеры и динамику.',
          'Контент Пользователя — бланки, данные профиля здоровья и иные материалы, которые Пользователь загружает или указывает в Сервисе.',
          'Материалы Health Lab KZ — дизайн, тексты, программный код, товарные знаки, AI-комментарии, интерфейсы и иные материалы, доступные через Сервис, за исключением персональных данных и Контента Пользователя.',
          'Сторонние сервисы — сервисы третьих лиц (хостинг, аналитика, распознавание бланков и подобное), которые могут привлекаться для работы Сервиса.',
        ),
      ],
    },
    {
      title: '3. Предмет соглашения',
      blocks: [
        t('Health Lab KZ предоставляет Пользователю доступ к личному health-кабинету, который позволяет:'),
        l(
          'загружать бланки с результатами анализов, преимущественно связанных с кровью, и заполнять профиль здоровья;',
          'получать распознанные маркеры на дашборде рядом с референсными нормами;',
          'видеть динамику маркеров во времени;',
          'получать образовательные AI-комментарии о том, что означает изменение конкретного маркера.',
        ),
      ],
    },
    {
      title: '4. Health Lab KZ не оказывает медицинских услуг',
      blocks: [
        t('4.1. Health Lab KZ — не медицинская организация. Сервис не оказывает медицинских услуг, не ставит диагнозы, не даёт медицинских заключений и не предоставляет рекомендаций по лечению или лекарствам ни через дашборд, ни через AI-комментарии, ни каким-либо иным образом. Все материалы, распознанные маркеры и AI-комментарии предоставляются исключительно в информационных и образовательных целях.'),
        t('4.2. Сервис не заменяет консультацию врача. Health Lab KZ рекомендует Пользователю обращаться за консультацией к квалифицированному врачу при отклонении маркера от нормы, значимом изменении динамики или наличии любых вопросов о своём здоровье, и настоятельно рекомендует не откладывать обращение за медицинской помощью, полагаясь на данные Сервиса. При подозрении на неотложное состояние Пользователю следует немедленно обратиться за экстренной медицинской помощью.'),
        t('4.3. Использование Сервиса не создаёт отношений «врач — пациент» между Пользователем и Health Lab KZ.'),
      ],
    },
    {
      title: '5. Осторожность при использовании данных и маркеров',
      blocks: [
        t('5.1. Пользователь понимает, что распознанные маркеры и данные профиля здоровья могут отражать неожиданную или беспокоящую информацию, и что окончательную интерпретацию таких данных должен давать врач.'),
        t('5.2. Точность автоматического распознавания бланков зависит от качества и полноты загружаемых материалов. Health Lab KZ прилагает разумные усилия для корректного распознавания, но не гарантирует полное отсутствие ошибок распознавания. Пользователь вправе обратиться в поддержку, если считает, что распознавание бланка или интерпретация маркера содержит ошибку.'),
        t('5.3. Пользователь самостоятельно принимает решение о том, каким образом и с кем делиться своими маркерами, бланками и иными сведениями о здоровье, полученными через Сервис.'),
      ],
    },
    {
      title: '6. Кто может пользоваться Сервисом',
      blocks: [
        t('6.1. Для использования Сервиса Пользователь должен обладать необходимой право- и дееспособностью в соответствии с законодательством Республики Казахстан.'),
        t('6.2. Использование Сервиса лицом младше 18 лет допускается с согласия законного представителя (родителя или опекуна). Учитывая, что Сервис работает с данными о здоровье, Health Lab KZ рекомендует законным представителям контролировать использование Сервиса несовершеннолетними.'),
        t('6.3. Health Lab KZ вправе отказать в доступе к Сервису или ограничить доступ Пользователя, если он ранее нарушал настоящие Условия, действует от имени другого лица без соответствующих полномочий или иным образом не соответствует условиям использования Сервиса.'),
      ],
    },
    {
      title: '7. Регистрация и аккаунт',
      blocks: [
        // Расходится с текстом юриста сознательно — см. шапку файла и §5 Политики.
        t('7.1. Для использования основных функций Сервиса Пользователь создаёт Аккаунт, указывая адрес электронной почты, пароль, имя и фамилию. Пол, дата рождения и иные сведения о здоровье указываются Пользователем в профиле здоровья после регистрации, в объёме, предусмотренном Политикой конфиденциальности.'),
        t('7.2. Пользователь обязан предоставлять достоверные данные и своевременно их обновлять.'),
        t('7.3. Пользователь несёт ответственность за сохранность доступа к своему Аккаунту и за все действия, совершённые под его Аккаунтом. Пользователь обязуется незамедлительно уведомить Health Lab KZ о любом случае несанкционированного доступа к Аккаунту.'),
      ],
    },
    {
      title: '8. Лицензия на использование Сервиса',
      blocks: [
        t('8.1. При условии соблюдения настоящих Условий Health Lab KZ предоставляет Пользователю ограниченную, неисключительную, непередаваемую и отзывную лицензию на доступ к Сервису и его использование для личных некоммерческих целей.'),
        t('8.2. Пользователю запрещается копировать, воспроизводить, модифицировать, декомпилировать или распространять Материалы Health Lab KZ, а также использовать автоматизированные средства (боты, парсеры, скраперы) для взаимодействия с Сервисом без отдельного письменного разрешения Health Lab KZ, кроме случаев, прямо разрешённых законодательством Республики Казахстан.'),
      ],
    },
    {
      title: '9. Контент Пользователя и права на него',
      blocks: [
        t('9.1. Пользователь сохраняет права на Контент, который он загружает или указывает в Сервисе (включая бланки и данные профиля здоровья).'),
        t('9.2. Загружая Контент, Пользователь предоставляет Health Lab KZ право обрабатывать этот Контент в объёме, необходимом для: (а) предоставления функций Сервиса (распознавание маркеров, построение динамики, формирование AI-комментариев); (б) развития и улучшения моделей распознавания и интерпретации маркеров, включая использование обезличенных данных для исследований и повышения качества Сервиса, — в соответствии с Политикой конфиденциальности.'),
        t('9.3. Пользователь гарантирует, что обладает правами на загружаемый Контент и что загрузка Контента не нарушает права третьих лиц (в частности, Пользователь не должен загружать бланки других людей без их согласия).'),
      ],
    },
    {
      title: '10. AI-функции и автоматизированные интерпретации',
      blocks: [
        t('10.1. Health Lab KZ использует автоматизированное распознавание бланков и генеративные AI-инструменты для формирования образовательных комментариев к маркерам.'),
        t('10.2. AI-комментарии формируются автоматически на основе загруженных Пользователем данных. Health Lab KZ не контролирует содержание Контента Пользователя, на основании которого формируется AI-комментарий, и не гарантирует, что AI-комментарий будет исчерпывающим или полностью точным для конкретной ситуации Пользователя.'),
        t('10.3. AI-комментарии не являются заменой человека-врача и не должны восприниматься как окончательное суждение о состоянии здоровья Пользователя. Пользователь обязуется использовать собственное суждение и консультацию с врачом при оценке любых AI-комментариев, полученных через Сервис.'),
      ],
    },
    {
      title: '11. Права и обязанности Пользователя',
      blocks: [
        t('Пользователь вправе:'),
        l(
          'использовать функции Сервиса в соответствии с их назначением;',
          'загружать бланки и заполнять профиль здоровья;',
          'получать доступ к своим маркерам, динамике и AI-комментариям;',
          'реализовывать права в отношении своих персональных данных согласно Политике конфиденциальности.',
        ),
        t('Пользователь обязуется не совершать следующих действий:'),
        l(
          'загружать бланки и иной Контент, принадлежащие другим лицам, без их согласия;',
          'использовать Сервис в противоправных целях, включая мошенничество, выдачу себя за другое лицо или предоставление заведомо ложных данных;',
          'нарушать работу Сервиса, включая попытки несанкционированного доступа к системам Health Lab KZ, распространение вредоносного кода или атаки на инфраструктуру Сервиса;',
          'размещать оскорбительный, дискриминационный, незаконный или иным образом ненадлежащий контент в тех функциях Сервиса, где это применимо;',
          'выдавать себя за представителя Health Lab KZ или иным образом создавать ложное впечатление об аффилированности с Health Lab KZ.',
        ),
        t('При нарушении данных обязательств Health Lab KZ вправе ограничить или прекратить доступ Пользователя к Сервису в порядке, предусмотренном разделом 18.'),
      ],
    },
    {
      title: '12. Права и обязанности Health Lab KZ',
      blocks: [
        t('Health Lab KZ вправе:'),
        l(
          'изменять функциональность Сервиса, включая добавление, изменение или прекращение отдельных функций, с уведомлением Пользователя о существенных изменениях;',
          'приостановить или прекратить доступ Пользователя к Аккаунту при нарушении условий настоящих Условий;',
          'запрашивать у Пользователя дополнительную информацию, необходимую для оказания услуг.',
        ),
        t('Health Lab KZ обязуется:'),
        l(
          'обеспечивать работу Сервиса в соответствии с его функциональным назначением;',
          'обрабатывать персональные данные Пользователя в соответствии с Политикой конфиденциальности и законодательством Республики Казахстан;',
          'информировать Пользователя о существенных изменениях Условий.',
        ),
      ],
    },
    {
      title: '13. Персональные данные',
      blocks: [
        t('Обработка персональных данных Пользователя, включая сведения о здоровье, осуществляется в соответствии с Политикой конфиденциальности Health Lab KZ.'),
      ],
    },
    {
      title: '14. Сторонние сервисы',
      blocks: [
        t('14.1. Для работы Сервиса Health Lab KZ может привлекать Сторонние сервисы (хостинг, аналитику, инструменты распознавания бланков и генеративного AI). Такие сервисы обрабатывают данные от имени и по поручению Health Lab KZ в соответствии с Политикой конфиденциальности.'),
        t('14.2. Health Lab KZ не несёт ответственности за перебои в работе Сторонних сервисов, находящиеся вне разумного контроля Health Lab KZ, но принимает разумные меры для минимизации влияния таких перебоев на Пользователя.'),
      ],
    },
    {
      title: '15. Стоимость услуг',
      blocks: [
        t('15.1. На дату вступления в силу настоящих Условий все функции Сервиса предоставляются Пользователю бесплатно.'),
        t('15.2. Health Lab KZ вправе в будущем ввести платные функции. В этом случае условия оплаты, порядок возврата средств и иные связанные положения будут описаны в отдельном приложении к настоящим Условиям и доведены до Пользователя заблаговременно, до начала использования платных функций.'),
      ],
    },
    {
      title: '16. Отказ от гарантий',
      blocks: [
        t('16.1. Сервис предоставляется на условиях «как есть» и «по мере доступности». Health Lab KZ прилагает разумные усилия для обеспечения корректной и бесперебойной работы Сервиса, но не гарантирует отсутствие ошибок, сбоев или временной недоступности Сервиса.'),
        t('16.2. Health Lab KZ не гарантирует, что распознавание маркеров, динамика или AI-комментарии будут полностью точными, полными или актуальными для конкретной ситуации Пользователя, поскольку это зависит от качества загруженных Пользователем данных.'),
        t('16.3. Настоящий раздел не исключает и не ограничивает гарантии и права Пользователя, которые не могут быть исключены или ограничены в соответствии с законодательством Республики Казахстан, включая законодательство о защите прав потребителей.'),
      ],
    },
    {
      title: '17. Ограничение ответственности и возмещение вреда',
      blocks: [
        t('17.1. Health Lab KZ не несёт ответственности за решения, принятые Пользователем на основании информации, полученной через Сервис, включая распознанные маркеры и AI-комментарии, без консультации с врачом.'),
        t('17.2. Health Lab KZ не несёт ответственности за достоверность и качество бланков и иных данных, загружаемых Пользователем, а также за последствия загрузки Пользователем недостоверных или искажённых данных.'),
        t('17.3. Ответственность Health Lab KZ ограничивается случаями, прямо предусмотренными законодательством Республики Казахстан, и не распространяется на случаи умысла или грубой неосторожности Health Lab KZ, ответственность за которые не может быть ограничена в соответствии с законодательством Республики Казахстан.'),
        t('17.4. Пользователь возмещает Health Lab KZ подтверждённые убытки, возникшие вследствие нарушения Пользователем настоящих Условий или применимого законодательства, в порядке, предусмотренном законодательством Республики Казахстан.'),
      ],
    },
    {
      title: '18. Срок действия, изменение и прекращение Условий',
      blocks: [
        t('18.1. Условия действуют с момента акцепта Пользователем и до момента удаления Аккаунта либо иного прекращения использования Сервиса.'),
        t('18.2. Health Lab KZ вправе изменять Условия. Дата последнего обновления указывается в начале документа. Существенные изменения доводятся до Пользователя через приложение или иным доступным способом. Продолжение использования Сервиса после вступления изменений в силу означает согласие Пользователя с обновлённой редакцией.'),
        t('18.3. Пользователь вправе в любой момент прекратить использование Сервиса и удалить свой Аккаунт в порядке, предусмотренном Политикой конфиденциальности.'),
        t('18.4. Health Lab KZ вправе приостановить или прекратить доступ Пользователя к Аккаунту в случае нарушения Пользователем настоящих Условий, использования Сервиса в противоправных целях или поступления обоснованных требований уполномоченных государственных органов Республики Казахстан.'),
        t('18.5. Положения Условий, которые по своему смыслу предполагают действие после прекращения Условий (в частности, об ограничении ответственности, интеллектуальной собственности и разрешении споров), сохраняют силу после такого прекращения.'),
      ],
    },
    {
      title: '19. Уведомления',
      blocks: [
        t('19.1. Health Lab KZ вправе направлять Пользователю уведомления, связанные с Сервисом и настоящими Условиями, через приложение, электронную почту или иным разумным способом, указанным Пользователем при регистрации.'),
        t('19.2. Пользователь направляет обращения и уведомления Health Lab KZ по контактам, указанным в разделе 22.'),
      ],
    },
    {
      title: '20. Порядок разрешения споров',
      blocks: [
        t('20.1. Стороны принимают меры к разрешению споров и разногласий путём переговоров.'),
        t('20.2. Пользователь вправе направить Health Lab KZ претензию по контактам, указанным в разделе 22. Срок рассмотрения претензии — не более 30 (тридцати) календарных дней с даты получения.'),
        t('20.3. При невозможности урегулировать спор путём переговоров он подлежит разрешению в суде в соответствии с законодательством Республики Казахстан. Пользователь, являющийся потребителем, вправе обратиться в суд по месту своего жительства в соответствии с законодательством Республики Казахстан о защите прав потребителей.'),
      ],
    },
    {
      title: '21. Применимое право и прочие условия',
      blocks: [
        t('21.1. Настоящие Условия регулируются и толкуются в соответствии с законодательством Республики Казахстан, включая Гражданский кодекс Республики Казахстан, Закон Республики Казахстан «О защите прав потребителей» и Закон Республики Казахстан «О персональных данных и их защите».'),
        t('21.2. Если какое-либо положение Условий будет признано недействительным или неисполнимым, это не влияет на действительность остальных положений.'),
        t('21.3. Бездействие Health Lab KZ в отношении нарушения Пользователем каких-либо положений Условий не означает отказа от права потребовать исполнения в дальнейшем.'),
        t('21.4. Стороны освобождаются от ответственности за неисполнение обязательств по Условиям, если такое неисполнение вызвано обстоятельствами непреодолимой силы в соответствии с законодательством Республики Казахстан.'),
      ],
    },
    {
      title: '22. Реквизиты Health Lab KZ',
      blocks: [
        t('Индивидуальный предприниматель «Health Lab KZ»'),
        t(`Адрес: ${ADDRESS}`),
        t(`Email: ${LEGAL_CONTACT_EMAIL}`),
      ],
    },
  ],
};

export const legalDocs = { privacy: privacyPolicy, tos: termsOfUse } as const;

export type LegalKind = keyof typeof legalDocs;
